import React, { useCallback, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UploadCloud, X, Loader2, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { compressImageToWebP } from '../../../utils/imageUtils';
import { useUploadPrescription } from '../../../hooks/useCart';

interface PrescriptionUploaderProps {
    value: string | null; // The uploaded URL
    onChange: (url: string | null) => void;
    onUploading: (isUploading: boolean) => void;
}

export const PrescriptionUploader: React.FC<PrescriptionUploaderProps> = ({ value, onChange, onUploading }) => {
    const { t } = useTranslation('shop');
    const uploadMutation = useUploadPrescription();
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUpload = async (file: File) => {
        const MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
        if (file.size > MAX_SIZE) {
            toast.error(t('prescription.uploadError_tooBig', 'File exceeds 5MB size limit.'));
            return;
        }

        const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            toast.error(t('prescription.uploadError_invalidType', 'Invalid format. Only JPG, PNG, WebP, and PDF are supported.'));
            return;
        }

        try {
            onUploading(true);
            let fileToUpload = file;
            
            // Compress images, leave pass PDFs as-is
            if (file.type.startsWith('image/')) {
                fileToUpload = await compressImageToWebP(file, 0.8);
            }

            const response = await uploadMutation.mutateAsync(fileToUpload);

            if (response.success && response.url) {
                onChange(response.url);
                toast.success(t('prescription.uploadSuccess', 'Prescription uploaded successfully'));
            } else {
                toast.error(response.message || 'Upload failed');
            }
        } catch (error: any) {
            console.error("Prescription upload failed", error);
            toast.error(t('prescription.uploadFailed', 'Failed to upload prescription'));
        } finally {
            onUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemove = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(null);
    };

    const onDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const onDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files?.length) {
            // Only take the first file
            handleUpload(e.dataTransfer.files[0]);
        }
    }, []);

    const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.length) {
            handleUpload(e.target.files[0]);
        }
    };

    // If an image is already uploaded, show preview
    if (value) {
        const isPdf = value.endsWith('.pdf');
        
        return (
            <div className="relative group w-full h-48 rounded-xl border-2 border-primary bg-blue-50/30 overflow-hidden flex flex-col items-center justify-center p-4">
                {isPdf ? (
                    <div className="flex flex-col items-center justify-center text-center gap-3">
                        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center">
                            <FileText size={32} />
                        </div>
                        <div>
                            <p className="font-bold text-gray-900">{t('prescription.pdfUploaded', 'PDF Document Uploaded')}</p>
                            <a href={value} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline" onClick={e => e.stopPropagation()}>
                                {t('prescription.viewPdf', 'View Document')}
                            </a>
                        </div>
                    </div>
                ) : (
                    <img src={value} alt="Prescription" className="max-w-full max-h-full object-contain" />
                )}
                
                {/* Remove Overlay */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <button 
                        onClick={handleRemove}
                        className="bg-white text-red-600 font-bold px-4 py-2 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2"
                    >
                        <X size={18} />
                        {t('prescription.removeFile', 'Remove File')}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`w-full rounded-xl border-2 border-dashed transition-all p-8 flex flex-col items-center justify-center text-center cursor-pointer group ${
                isDragging
                    ? 'border-primary bg-primary/5'
                    : 'border-gray-300 hover:border-gray-400 bg-gray-50/50 hover:bg-gray-100/50'
            }`}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
        >
            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={onFileInput}
            />

            <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-4 transition-colors ${
                isDragging ? 'bg-primary text-white scale-110' : 'bg-white text-gray-400 shadow-sm group-hover:text-primary group-hover:scale-105'
            }`}>
                {uploadMutation.isPending ? (
                    <Loader2 size={24} className="animate-spin text-primary" />
                ) : (
                    <UploadCloud size={24} />
                )}
            </div>
            
            <h4 className="text-sm font-bold text-gray-900 mb-1">
                {uploadMutation.isPending 
                    ? t('prescription.uploading', 'Uploading...') 
                    : t('prescription.dragDrop', 'Drag & drop your prescription here')}
            </h4>
            <p className="text-xs text-gray-500 mb-4 max-w-xs">
                {t('prescription.uploadFormats', 'Accepted: JPG, PNG, WebP, PDF (max 5MB)')}
            </p>
            
            <button 
                type="button"
                disabled={uploadMutation.isPending}
                className="text-xs font-bold text-primary bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-md transition-colors"
                onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                }}
            >
                {t('prescription.orBrowse', 'Or browse files')}
            </button>
        </div>
    );
};
